import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Search, Briefcase, MapPin, Clock, ArrowLeft, Building2, User, Mail, Phone, Globe, Award, Star, Facebook, Instagram, Linkedin, Twitter } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useIsNativeApp } from '../hooks/useIsNativeApp';

import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATE_MAP = {
  AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',CO:'Colorado',
  CT:'Connecticut',DE:'Delaware',FL:'Florida',GA:'Georgia',HI:'Hawaii',ID:'Idaho',
  IL:'Illinois',IN:'Indiana',IA:'Iowa',KS:'Kansas',KY:'Kentucky',LA:'Louisiana',
  ME:'Maine',MD:'Maryland',MA:'Massachusetts',MI:'Michigan',MN:'Minnesota',MS:'Mississippi',
  MO:'Missouri',MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',NJ:'New Jersey',
  NM:'New Mexico',NY:'New York',NC:'North Carolina',ND:'North Dakota',OH:'Ohio',OK:'Oklahoma',
  OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',SD:'South Dakota',
  TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',VA:'Virginia',WA:'Washington',
  WV:'West Virginia',WI:'Wisconsin',WY:'Wyoming',
};

const ROLE_LABELS = { agent: 'Agent', broker: 'Broker', provider: 'Provider' };

export default function FindJobs() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState('NJ');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);

  const [expandedJobs, setExpandedJobs] = useState({});
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [profileType, setProfileType] = useState(null);
  const isNativeApp = useIsNativeApp();
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [bioExpanded, setBioExpanded] = useState(false);

  useEffect(() => { fetchJobs(); }, [state, search, page, perPage]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = { page, per_page: perPage };
      if (state && state !== 'all') params.state = state;
      if (search) params.q = search;
      const res = await axios.get(`${API}/public/jobs`, { params });
      setJobs(res.data.jobs || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch (e) {
      console.error('Failed to fetch jobs:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const openProfilePopup = async (postedBy) => {
    if (!postedBy?.id) return;
    const role = postedBy.role;
    const isProvider = role === 'provider';
    setSelectedProfile(postedBy.id);
    setProfileType(isProvider ? 'provider' : 'agent');
    setProfileLoading(true);
    setProfileData(null);
    setBioExpanded(false);

    try {
      const endpoint = isProvider ? `/public/provider/${postedBy.id}` : `/public/agent/${postedBy.id}`;
      const res = await axios.get(`${API}${endpoint}`);
      setProfileData(res.data);
    } catch (e) {
      console.error('Failed to load profile:', e);
      setProfileData(null);
    } finally {
      setProfileLoading(false);
    }
  };

  const closeProfile = () => {
    setSelectedProfile(null);
    setProfileData(null);
    setBioExpanded(false);
  };

  const timeAgo = (d) => {
    if (!d) return '';
    const diff = Date.now() - new Date(d).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(diff / 3600000);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(diff / 86400000);
    if (days < 7) return `${days}d ago`;
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCity = (c) => c ? c.replace(/\b\w/g, l => l.toUpperCase()) : '';
  const formatPhone = (p) => {
    if (!p) return '';
    const d = p.replace(/\D/g, '');
    if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
    return p;
  };

  return (
    <div className="min-h-screen bg-stone-50">

    {!isNativeApp && (
      <header className="bg-[#1A4D2E] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" className="text-white hover:bg-white/10" onClick={() => navigate('/')} data-testid="back-home-btn">
            <ArrowLeft size={18} />
          </Button>
          <div className="flex items-center gap-2">
            <Building2 size={22} className="text-[#D4A373]" />
            <h1 className="text-xl font-bold" style={{ fontFamily: '"Playfair Display", serif' }}>Find Jobs</h1>
          </div>
        </div>
      </header>

  )}

      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input placeholder="Search by title, description, or city..." value={searchInput} onChange={(e) => setSearchInput(e.target.value)} className="pl-10" data-testid="job-search-input" />
            </div>
            <Select value={state} onValueChange={(v) => { setState(v); setPage(1); }}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="job-state-filter">
                <SelectValue placeholder="Select State" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                {Object.entries(STATE_MAP).map(([abbr, name]) => (
                  <SelectItem key={abbr} value={abbr}>{name} ({abbr})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button type="submit" className="bg-[#1A4D2E] hover:bg-[#143D24]" data-testid="job-search-btn">
              <Search size={16} className="mr-2" /> Search
            </Button>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <p className="text-sm text-stone-600 mb-4" data-testid="job-results-count">
          {loading ? 'Loading...' : `${total} job${total !== 1 ? 's' : ''} found`}
          {state && state !== 'all' && ` in ${STATE_MAP[state] || state}`}
          {search && ` matching "${search}"`}
        </p>

        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Card key={i} className="p-5 border-l-4 border-stone-200">
                <Skeleton className="h-5 w-2/5 mb-2" />
                <div className="flex gap-4 mb-2">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-4 w-20" />
                </div>
                <Skeleton className="h-4 w-full mb-1" />
                <Skeleton className="h-4 w-3/4 mb-3" />
                <div className="pt-3 border-t border-stone-100">
                  <Skeleton className="h-3 w-36" />
                </div>
              </Card>
            ))}
          </div>
        ) : jobs.length === 0 ? (
          <Card className="p-12 text-center">
            <Briefcase className="text-stone-300 mx-auto mb-4" size={32} />
            <p className="text-stone-600 mb-1">No jobs found</p>
            <p className="text-sm text-stone-500">Try adjusting your filters or search terms</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {jobs.map((job) => (
              <Card key={job.id} className="p-5 hover:shadow-md transition-shadow border-l-4 border-[#1A4D2E]" data-testid={`public-job-${job.id}`}>
                <h3 className="text-lg font-semibold text-stone-900 mb-1">{job.title}</h3>
                <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-stone-500 mb-2">
                  <span className="flex items-center gap-1"><MapPin size={14} />{job.city}, {job.state}</span>
                  <span className="flex items-center gap-1"><Clock size={14} />{timeAgo(job.created_at)}</span>
                </div>
                <p className={`text-sm text-stone-700 ${expandedJobs[job.id] ? '' : 'line-clamp-2'}`}>{job.description}</p>
                {job.description.length > 120 && (
                  <button onClick={() => setExpandedJobs(prev => ({ ...prev, [job.id]: !prev[job.id] }))} className="text-xs font-medium text-[#1A4D2E] hover:underline" data-testid={`toggle-desc-${job.id}`}>
                    {expandedJobs[job.id] ? 'Less' : 'More'}
                  </button>
                )}
                <div className="flex items-center gap-2 pt-3 border-t border-stone-100">
                  <User size={14} className="text-stone-400" />
                  <span className="text-xs text-stone-500">
                    Posted by{' '}
                    <button
                      onClick={() => openProfilePopup(job.posted_by)}
                      className="font-medium text-[#1A4D2E] hover:underline cursor-pointer"
                      data-testid={`poster-link-${job.id}`}
                    >
                      {job.posted_by?.name}
                    </button>
                    {job.posted_by?.role && (
                      <span className="ml-1 px-1.5 py-0.5 bg-stone-100 rounded text-[10px] uppercase tracking-wider">
                        {ROLE_LABELS[job.posted_by.role] || job.posted_by.role}
                      </span>
                    )}
                  </span>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {total > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-stone-200 mt-4" data-testid="pagination">
            <div className="flex items-center gap-2 text-sm text-stone-600">
              <span>Show</span>
              <select
                value={perPage}
                onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                className="border border-stone-300 rounded px-2 py-1 text-sm bg-white"
                data-testid="per-page-select"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span>per page</span>
              <span className="text-stone-400 ml-2">|</span>
              <span className="ml-2">{(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} of {total}</span>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(1)} data-testid="page-first">First</Button>
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="page-prev">Prev</Button>
              <span className="px-3 py-1 text-sm font-medium text-stone-700">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="page-next">Next</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(totalPages)} data-testid="page-last">Last</Button>
            </div>
          </div>
        )}
      </div>

      {/* Profile Popup */}
      <Dialog open={!!selectedProfile} onOpenChange={(open) => { if (!open) closeProfile(); }}>
        <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto p-0 gap-0" data-testid="profile-popup" aria-describedby="profile-popup-desc">
          {profileLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1A4D2E]" />
            </div>
          ) : profileData ? (() => {
            const isAgent = profileType === 'agent';
            const accentBg = isAgent ? 'bg-[#1A4D2E]' : 'bg-purple-600';
            const accentText = isAgent ? 'text-[#1A4D2E]' : 'text-purple-600';
            const profile = profileData.profile || {};
            const name = profileData.name || profileData.full_name || '';
            const socialMedia = profile.social_media || {};
            const hasSocials = Object.values(socialMedia).some(v => v);
            const avgRating = profile.average_rating || profileData.average_rating || 0;
            const totalReviews = profile.total_reviews || profileData.total_reviews || 0;
            const reviews = profileData.reviews || [];
            const topReviews = reviews.filter(r => r.rating >= 4).sort((a, b) => b.rating - a.rating).slice(0, 3);

            return (
              <>
                <DialogHeader className="sr-only">
                  <DialogTitle>Profile</DialogTitle>
                  <p id="profile-popup-desc">Professional profile details</p>
                </DialogHeader>

                <div className="px-6 pt-6 pb-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 rounded-full ${accentBg} flex items-center justify-center text-white font-bold text-xl flex-shrink-0`}>
                      {name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="font-semibold text-lg text-stone-900" data-testid="profile-popup-name">{name}</h2>
                      {isAgent && profileData.role && <p className="text-sm text-stone-600 capitalize">{profileData.role}</p>}
                      {isAgent && (profileData.title || profile.title) && <p className="text-sm text-stone-500 italic">{profileData.title || profile.title}</p>}
                      {!isAgent && profileData.company_name && <p className="text-sm text-stone-600">{profileData.company_name}</p>}
                      {!isAgent && profileData.service_type && <p className={`text-sm ${accentText} font-medium`}>{profileData.service_type}</p>}
                      {profile.agency_name && <p className="text-sm text-stone-500 flex items-center gap-1 mt-0.5"><Building2 size={13} /> {profile.agency_name}</p>}
                      {avgRating > 0 && (
                        <div className="flex items-center gap-0.5 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 mt-2 w-fit">
                          <Star size={13} className="text-amber-500 fill-amber-500" />
                          <span className="text-sm font-semibold text-stone-800">{avgRating.toFixed(1)}</span>
                          <span className="text-xs text-stone-500">({totalReviews})</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="px-6 py-4 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm" data-testid="profile-popup-contact">
                    {(profileData.city || profileData.state) && (
                      <div className="flex items-center gap-2 text-stone-700">
                        <MapPin size={15} className={`${accentText} flex-shrink-0`} />
                        <span className="truncate">{[formatCity(profileData.city), profileData.state, profileData.zip_code].filter(Boolean).join(', ')}</span>
                      </div>
                    )}
                    {profileData.email && (
                      <div className="flex items-center gap-2 text-stone-700">
                        <Mail size={15} className={`${accentText} flex-shrink-0`} />
                        <a href={`mailto:${profileData.email}`} className="hover:underline truncate">{profileData.email}</a>
                      </div>
                    )}
                    {profileData.phone && (
                      <div className="flex items-center gap-2 text-stone-700">
                        <Phone size={15} className={`${accentText} flex-shrink-0`} />
                        <a href={`tel:${profileData.phone}`}>{formatPhone(profileData.phone)}</a>
                      </div>
                    )}
                    {profileData.years_of_experience && (
                      <div className="flex items-center gap-2 text-stone-700">
                        <Award size={15} className={`${accentText} flex-shrink-0`} />
                        <span>{profileData.years_of_experience} years experience</span>
                      </div>
                    )}
                    {(profileData.website || profile.website) && (
                      <div className="flex items-center gap-2 text-stone-700">
                        <Globe size={15} className={`${accentText} flex-shrink-0`} />
                        {(() => {
                          const w = profileData.website || profile.website;
                          return <a href={w.startsWith('http') ? w : `https://${w}`} target="_blank" rel="noopener noreferrer" className="hover:underline truncate">{w}</a>;
                        })()}
                      </div>
                    )}
                  </div>

                  {hasSocials && (
                    <div className="flex items-center gap-3 pt-1">
                      {socialMedia.facebook && <a href={`https://facebook.com/${socialMedia.facebook}`} target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-blue-600 transition-colors"><Facebook size={18} /></a>}
                      {socialMedia.instagram && <a href={`https://instagram.com/${socialMedia.instagram}`} target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-pink-600 transition-colors"><Instagram size={18} /></a>}
                      {socialMedia.linkedin && <a href={`https://linkedin.com/in/${socialMedia.linkedin}`} target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-blue-700 transition-colors"><Linkedin size={18} /></a>}
                      {socialMedia.twitter && <a href={`https://twitter.com/${socialMedia.twitter}`} target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-sky-500 transition-colors"><Twitter size={18} /></a>}
                    </div>
                  )}

                  {(profileData.bio || profileData.about || profile.bio) && (
                    <div className="pt-3 border-t border-stone-100">
                      <h3 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-1.5">About</h3>
                      <p className={`text-sm text-stone-700 leading-relaxed whitespace-pre-line ${!bioExpanded ? 'line-clamp-3' : ''}`}>{profileData.bio || profileData.about || profile.bio}</p>
                      {!bioExpanded && <button onClick={() => setBioExpanded(true)} className={`text-sm font-medium ${accentText} hover:underline mt-1`}>more</button>}
                    </div>
                  )}

                  {topReviews.length > 0 && (
                    <div className="pt-3 border-t border-stone-100">
                      <h3 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2">Top Reviews</h3>
                      <div className="space-y-2">
                        {topReviews.map((review, idx) => (
                          <div key={idx} className="p-3 bg-amber-50/60 rounded-lg border border-amber-100">
                            <div className="flex items-center gap-1 mb-1">
                              {[...Array(5)].map((_, i) => <Star key={i} size={12} className={i < review.rating ? 'text-amber-500 fill-amber-500' : 'text-stone-300'} />)}
                              {review.reviewer_name && <span className="text-xs font-medium text-stone-500 ml-2">{review.reviewer_name}</span>}
                            </div>
                            {(review.review_text || review.comment) && <p className="text-sm text-stone-700 line-clamp-2">{review.review_text || review.comment}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="pt-4 border-t border-stone-100 flex gap-2 pb-2">
                    <Button
                      onClick={() => navigate(isAgent ? `/agent/${selectedProfile}/view` : `/provider/${selectedProfile}/view`)}
                      className={`flex-1 ${accentBg} text-white hover:opacity-90`}
                      size="sm"
                      data-testid="profile-popup-view-full"
                    >
                      View Full Profile
                    </Button>
                    <Button onClick={closeProfile} variant="outline" size="sm" data-testid="profile-popup-close">Close</Button>
                  </div>
                </div>
              </>
            );
          })() : (
            <div className="p-8 text-center text-stone-500">Profile not found</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
